import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { mediaDeleteSuccessToast, mediaEditSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface MediaDetail {
  id: string;
  title?: string;
  filename: string;
  name?: string;
  mimeType?: string;
  size?: number;
  humanSize?: string;
  createdAt?: string;
  folder?: { name: string };
}

function formatSize(bytes?: number, human?: string) {
  if (human) return human;
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function displayName(file: Pick<MediaDetail, 'title' | 'name' | 'filename'>) {
  return file.title ?? file.name ?? file.filename ?? 'Archivo';
}

function mediaContentUrl(id: string) {
  return `/api/media/files/${id}/content`;
}

export function MediaShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(PERMISSIONS.MULTIMEDIA_ELIMINAR);
  const canEdit = hasPermission(PERMISSIONS.MULTIMEDIA_EDITAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [title, setTitle] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['media', id],
    queryFn: () => api<MediaDetail>(`/media/${id}`),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (data) setTitle(displayName(data));
  }, [data]);

  const editMutation = useMutation({
    mutationFn: (nextTitle: string) =>
      api(`/media/files/${id}`, { method: 'PATCH', body: { title: nextTitle } }),
    onSuccess: async () => {
      toast(mediaEditSuccessToast());
      await queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo editar el archivo'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/media/files/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setDeleteOpen(false);
      toast(mediaDeleteSuccessToast());
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      navigate('/media');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el archivo'));
    },
  });

  const name = data ? displayName(data) : 'Archivo';
  const sizeLabel = formatSize(data?.size, data?.humanSize);
  const contentUrl = id ? mediaContentUrl(id) : undefined;

  function saveTitle(event: FormEvent) {
    event.preventDefault();
    const nextTitle = title.trim();
    if (!nextTitle) {
      toast({ title: 'El nombre del archivo es obligatorio.', variant: 'danger' });
      return;
    }
    if (nextTitle === name) return;
    editMutation.mutate(nextTitle);
  }

  return (
    <div className="lms-media-detail lms-site-admin-hub-page font-sans">
      <header className="lms-media-detail__header">
        <div className="lms-media-detail__heading">
          <HubBackLink to="/media" label="Volver a biblioteca multimedia" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{name}</h1>
          </div>
        </div>
      </header>

      {error && (
        <p className="lms-media-detail__error" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="lms-media-detail__loading">
          <Loader />
        </div>
      ) : (
        <div className="lms-media-detail__grid">
          <div className="lms-media-detail__main">
            <section className="lms-media-detail__card">
              <h2 className="lms-media-detail__card-title">Resumen</h2>
              <div className="lms-media-detail__summary">
                <div className="lms-media-detail__field lms-media-detail__field--full">
                  <p className="lms-media-detail__label">Nombre</p>
                  {canEdit ? (
                    <form className="lms-media-detail__name-form" onSubmit={saveTitle}>
                      <input
                        className="lms-media-detail__name-input"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        aria-label="Nombre del archivo"
                        maxLength={255}
                      />
                      <button
                        type="submit"
                        className="lms-media-detail__name-save"
                        disabled={editMutation.isPending || title.trim() === name}
                      >
                        {editMutation.isPending ? 'Guardando…' : 'Guardar'}
                      </button>
                    </form>
                  ) : (
                    <p className="lms-media-detail__value lms-media-detail__value--break">{name}</p>
                  )}
                </div>
                <div className="lms-media-detail__field">
                  <p className="lms-media-detail__label">Tipo</p>
                  <span className="lms-media-detail__mime-badge">{data?.mimeType ?? '—'}</span>
                </div>
                <div className="lms-media-detail__field">
                  <p className="lms-media-detail__label">Tamaño</p>
                  <p className="lms-media-detail__value">{sizeLabel}</p>
                </div>
              </div>
            </section>

            <section className="lms-media-detail__card">
              <h2 className="lms-media-detail__card-title">Información Técnica</h2>
              <div className="lms-media-detail__tech">
                <div className="lms-media-detail__field">
                  <p className="lms-media-detail__label">Carpeta</p>
                  <div className="lms-media-detail__folder">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      folder
                    </span>
                    <span>{data?.folder?.name ?? 'Raíz'}</span>
                  </div>
                </div>
                <div className="lms-media-detail__field">
                  <p className="lms-media-detail__label">Subido</p>
                  <p className="lms-media-detail__value">
                    {data?.createdAt ? new Date(data.createdAt).toLocaleString('es') : '—'}
                  </p>
                </div>
              </div>
            </section>
          </div>

          <aside className="lms-media-detail__aside">
            <section className="lms-media-detail__card lms-media-detail__actions-card">
              <div className="lms-media-detail__actions-stack">
                {contentUrl && (
                  <>
                    <a
                      href={contentUrl}
                      download
                      className="lms-media-detail__action-btn lms-media-detail__action-btn--primary"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        download
                      </span>
                      Descargar
                    </a>
                    <a
                      href={contentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="lms-media-detail__action-btn lms-media-detail__action-btn--secondary"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        visibility
                      </span>
                      Vista previa
                    </a>
                  </>
                )}
                <button
                  type="button"
                  className="lms-media-detail__action-btn lms-media-detail__action-btn--neutral"
                  disabled
                  title="Próximamente"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    cloud_upload
                  </span>
                  Reemplazar archivo
                </button>
                {canDelete && (
                  <button
                    type="button"
                    className="lms-media-detail__action-btn lms-media-detail__action-btn--danger"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      delete
                    </span>
                    Eliminar
                  </button>
                )}
              </div>
            </section>

            <section className="lms-media-detail__card">
              <div className="lms-media-detail__versions-header">
                <h3 className="lms-media-detail__usages-title">Versiones</h3>
                <button type="button" className="lms-media-detail__history-btn" aria-label="Historial de versiones" disabled>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    history
                  </span>
                </button>
              </div>
              <div className="lms-media-detail__version-row">
                <div className="lms-media-detail__version-main">
                  <span className="lms-media-detail__version-dot" aria-hidden="true" />
                  <span className="lms-media-detail__value">
                    v1 <span className="lms-media-detail__version-current">(actual)</span>
                  </span>
                </div>
                <span className="lms-media-detail__label lms-media-detail__label--normal">{sizeLabel}</span>
              </div>
            </section>

            <section className="lms-media-detail__card">
              <h3 className="lms-media-detail__usages-title">Lugares de Uso</h3>
              <p className="lms-media-detail__empty-text">Sin referencias</p>
            </section>
          </aside>
        </div>
      )}

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar archivo"
        description={`¿Está seguro de eliminar el archivo «${name}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteOpen(false);
        }}
      />
    </div>
  );
}
